/**
 * 机型识别：把 HEARTBEAT 里的 `type`（MAV_TYPE）与 `autopilot`（MAV_AUTOPILOT）
 * 翻译成"这是哪种载具"，因为后面所有领域知识（模式表、指令支持、单位习惯）
 * 都按载具类别分派。
 *
 * ## 为什么类别不是 MAV_TYPE 一比一
 *
 * MAVLink 的 MAV_TYPE 描述的是**机架**，ArduPilot 描述的是**固件**。
 * ArduPilot 只有六个固件：Copter / Plane / Rover / Sub / AntennaTracker / Blimp，
 * 但 Copter 一族就对应 MAV_TYPE 里的 9 个值（多旋翼的各种机架、共轴、直升机、
 * 十二旋翼、十旋翼、通用多旋翼），Plane 一族对应 9 个 VTOL 值 + 固定翼。
 * 界面关心的是"用哪个模式表"，所以这里按**固件**归类，不按机架归类。
 *
 * ## 判定策略（重要）
 *
 * 1. **机型只由 MAV_TYPE 决定**，不看 autopilot 字段。
 * 2. `MAV_AUTOPILOT` 只回答一个问题："这是不是 ArduPilot 固件"。
 *    正常值是 3（`MAV_AUTOPILOT_ARDUPILOTMEGA`），但**同一架飞机可能报别的值**：
 *    某些转发链路、第三方地面站配置、以及被手工改过的参数都会让它变成
 *    0（GENERIC）或 6/7（GENERIC_WAYPOINTS*）。
 *    因此 `isArduPilotFirmware === false` **不影响**机型识别，
 *    只把 `confident` 降为 false，让界面提示"未确认 ArduPilot 固件"。
 *    如果反过来"autopilot ≠ 3 就判为未知机型"，一个把 autopilot 改成 GENERIC
 *    的常见配置就会让整个地面站失去模式表 —— 代价远大于收益。
 * 3. MAV_TYPE 未知（`MAV_TYPE_GCS`、`MAV_TYPE_CAMERA`，或上游将来新增的值）
 *    一律返回 `'unknown'`，**不抛异常**：HEARTBEAT 是几十赫兹的广播，
 *    抛异常会把遥测解析整条链路炸掉；而且上游随时会加新的 MAV_TYPE，
 *    返回 unknown 让界面显示原始数字，用户看得见"上游新增了什么"。
 */

import { enumEntryByValue, enumValue } from '@hgc/mavlink';

/**
 * 载具类别 —— 与 ArduPilot 的固件一一对应。
 *
 * `'unknown'` 是一等公民，不是错误：多机场景里总会混进地面站、
 * 云台、相机这类也发 HEARTBEAT 的组件。
 */
export type VehicleKind = 'copter' | 'plane' | 'rover' | 'sub' | 'tracker' | 'blimp' | 'unknown';

/** 有专属固件的类别（不含 `'unknown'`），遍历用。 */
export const VEHICLE_KINDS: readonly Exclude<VehicleKind, 'unknown'>[] = [
  'copter',
  'plane',
  'rover',
  'sub',
  'tracker',
  'blimp',
];

/** 类别在界面上的中文名。 */
export const VEHICLE_KIND_LABELS: Readonly<Record<VehicleKind, string>> = {
  copter: '多旋翼',
  plane: '固定翼',
  rover: '车/船',
  sub: '潜水器',
  tracker: '天线跟踪器',
  blimp: '飞艇',
  unknown: '未知机型',
};

/**
 * ArduPilot 固件的 autopilot 值。数值取自上游 `MAV_AUTOPILOT`，
 * 这里只在注释里写出 3，实际比较用 {@link enumValue} 解析，避免手抄。
 */
function arduPilotAutopilotValue(): number | null {
  return enumValue('MAV_AUTOPILOT', 'MAV_AUTOPILOT_ARDUPILOTMEGA');
}

/**
 * 每个类别对应的 MAV_TYPE 条目名。
 *
 * 用**条目名**而不是数字：数字是 `@hgc/mavlink` 的生成产物里来的，
 * 手抄一遍 13/14/15 之类很容易错位，而名字在 code review 时一眼就能核。
 *
 * 归类依据是上游对每个 MAV_TYPE 的描述（`packages/mavlink/definitions/v1.0/common.xml`）：
 * - 多旋翼一族：Quadrotor / Coaxial helicopter / Normal helicopter with tail rotor /
 *   Hexarotor / Octorotor / Tricopter / Dodecarotor / Decarotor /
 *   Generic multirotor。ArduCopter 的 `AP_Motors` 支持其中的
 *   QUAD/HEXA/Y6/OCTA/OCTAQUAD/DODECAHEXA/DECA/TRI/SINGLE 等机架。
 * - 固定翼一族：Fixed wing + 全部 VTOL 变体（Tailsitter / Tiltrotor / Fixedrotor /
 *   Tiltwing / Gyrodyne）。VTOL 在 ArduPilot 里由 ArduPlane 的 QuadPlane 实现，
 *   模式表就是 `PLANE_MODE`（QSTABILIZE / QLOITER / …）。
 * - 车/船：Ground rover 与 Surface vessel —— 都是 ArduRover 固件。
 * - **飞艇不在这里**：上游 MAVLink 没有 `MAV_TYPE_BLIMP`，
 *   ArduBlimp 报的是 `MAV_TYPE_AIRSHIP`（见 {@link MAV_TYPE_BLIMP_NOTE}）。
 * - `MAV_TYPE_FLAPPING_WING` / `MAV_TYPE_KITE` / `MAV_TYPE_PARAFOIL` 故意不归类：
 *   它们是"带空气动力面的东西"，但 ArduPilot 没有对应固件，
 *   硬归到 plane 会给出一个不存在的模式表。
 */
const MAV_TYPE_NAMES_BY_KIND: Readonly<Record<Exclude<VehicleKind, 'unknown'>, readonly string[]>> = {
  copter: [
    'MAV_TYPE_QUADROTOR',
    'MAV_TYPE_COAXIAL',
    'MAV_TYPE_HELICOPTER',
    'MAV_TYPE_HEXAROTOR',
    'MAV_TYPE_OCTOROTOR',
    'MAV_TYPE_TRICOPTER',
    'MAV_TYPE_DODECAROTOR',
    'MAV_TYPE_DECAROTOR',
    'MAV_TYPE_GENERIC_MULTIROTOR',
  ],
  plane: [
    'MAV_TYPE_FIXED_WING',
    'MAV_TYPE_VTOL_TAILSITTER_DUOROTOR',
    'MAV_TYPE_VTOL_TAILSITTER_QUADROTOR',
    'MAV_TYPE_VTOL_TILTROTOR',
    'MAV_TYPE_VTOL_FIXEDROTOR',
    'MAV_TYPE_VTOL_TAILSITTER',
    'MAV_TYPE_VTOL_TILTWING',
    'MAV_TYPE_VTOL_RESERVED5',
    'MAV_TYPE_VTOL_GYRODYNE',
  ],
  rover: ['MAV_TYPE_GROUND_ROVER', 'MAV_TYPE_SURFACE_BOAT'],
  sub: ['MAV_TYPE_SUBMARINE'],
  tracker: ['MAV_TYPE_ANTENNA_TRACKER'],
  blimp: ['MAV_TYPE_AIRSHIP'],
};

/** 飞艇归类的依据，附在文档里方便日后核对。 */
export const MAV_TYPE_BLIMP_NOTE =
  'ArduBlimp 的 `Blimp/system.cpp::get_frame_mav_type()` 返回 `MAV_TYPE_AIRSHIP`；' +
  '核对日期 2026-09-25，来源 https://raw.githubusercontent.com/ArduPilot/ardupilot/master/Blimp/system.cpp';

let mavTypeToKindIndex: Map<number, VehicleKind> | null = null;

function mavTypeToKindMap(): Map<number, VehicleKind> {
  if (mavTypeToKindIndex !== null) return mavTypeToKindIndex;
  const map = new Map<number, VehicleKind>();
  for (const kind of VEHICLE_KINDS) {
    for (const entryName of MAV_TYPE_NAMES_BY_KIND[kind]) {
      const value = enumValue('MAV_TYPE', entryName);
      if (value === null) {
        // 上游删掉/改名了某个 MAV_TYPE。静默跳过而不是抛错：
        // 这里的目标是"尽力识别"，而不是"定义一套必须完整的数据"。
        continue;
      }
      map.set(value, kind);
    }
  }
  mavTypeToKindIndex = map;
  return map;
}

/** MAV_TYPE 数值 → 载具类别。未知类型返回 `'unknown'`。 */
export function vehicleKindOf(mavType: number): VehicleKind {
  return mavTypeToKindMap().get(mavType) ?? 'unknown';
}

/** MAV_TYPE 数值 → 上游条目名（如 `MAV_TYPE_QUADROTOR`）。未知返回 `undefined`。 */
export function mavTypeName(mavType: number): string | undefined {
  return enumEntryByValue('MAV_TYPE', mavType)?.name;
}

/** MAV_AUTOPILOT 数值 → 上游条目名。未知返回 `undefined`。 */
export function mavAutopilotName(mavAutopilot: number): string | undefined {
  return enumEntryByValue('MAV_AUTOPILOT', mavAutopilot)?.name;
}

/**
 * 该 autopilot 值是否表示 ArduPilot 固件。
 *
 * 只认 `MAV_AUTOPILOT_ARDUPILOTMEGA`（当前上游是 3）。
 * 特意**不**把 GENERIC(0) 也算进来：那样会让所有用 GENERIC 的固件
 * （PX4、任意自制飞控）都被当成 ArduPilot，模式表就会张冠李戴。
 * "未确认 ArduPilot 固件"是一个可展示的状态，而"错误地当成 ArduPilot"
 * 不是 —— 后者会让界面把一个 PX4 的模式号解释成 Copter 的 LOITER。
 */
export function isArduPilotAutopilot(mavAutopilot: number): boolean {
  const arduPilot = arduPilotAutopilotValue();
  return arduPilot !== null && mavAutopilot === arduPilot;
}

/** 一次识别出来的完整结论。 */
export interface VehicleIdentity {
  readonly kind: VehicleKind;
  readonly kindLabel: string;
  readonly mavType: number;
  readonly mavTypeName: string | undefined;
  readonly mavAutopilot: number;
  readonly mavAutopilotName: string | undefined;
  /** autopilot 字段是否明确写着 ArduPilot。false 时仍会给出机型，只是不可全信。 */
  readonly isArduPilotFirmware: boolean;
  /** 机型已知 **且** 固件已确认是 ArduPilot 时才为 true。 */
  readonly confident: boolean;
}

/**
 * 从 HEARTBEAT 的两个字段识别载具。
 *
 * 永远不抛异常、永远返回一个对象：遥测解析路径上的函数必须对任何输入都给出结论，
 * 因为对端可能是任何东西（甚至是别的项目误发的字节流被 CRC 侥幸通过）。
 * 无法识别时 `kind` 是 `'unknown'`，`mavTypeName` 保留原始数值供界面显示。
 */
export function identifyVehicle(mavType: number, mavAutopilot: number): VehicleIdentity {
  const kind = vehicleKindOf(mavType);
  const isArduPilotFirmware = isArduPilotAutopilot(mavAutopilot);
  return {
    kind,
    kindLabel: VEHICLE_KIND_LABELS[kind],
    mavType,
    mavTypeName: mavTypeName(mavType),
    mavAutopilot,
    mavAutopilotName: mavAutopilotName(mavAutopilot),
    isArduPilotFirmware,
    confident: kind !== 'unknown' && isArduPilotFirmware,
  };
}
